// Portal Page Component
// Public viewer for custom portal pages
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import pagesService from '../../services/pagesService';
import PortalLayout from './PortalLayout';
import { Eye } from 'lucide-react';

// Import block view components (read-only versions)
import TextBlockView from '../blocks/views/TextBlockView';
import ImageBlockView from '../blocks/views/ImageBlockView';
import CalendarBlockView from '../blocks/views/CalendarBlockView';
import ClassesBlockView from '../blocks/views/ClassesBlockView';
import ProjectsBlockView from '../blocks/views/ProjectsBlockView';
import DocumentsBlockView from '../blocks/views/DocumentsBlockView';
import TutorialsBlockView from '../blocks/views/TutorialsBlockView';
import HeroBlockView from '../blocks/views/HeroBlockView';
import NewsBlockView from '../blocks/views/NewsBlockView';
import ClassScheduleBlockView from '../blocks/views/ClassScheduleBlockView';
import BackgroundBlockView from '../blocks/views/BackgroundBlockView';

const GRID_COLS = 48; // Must match PageBuilder

const PortalPage = () => {
  const { userId, slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [portalSettings, setPortalSettings] = useState({});

  useEffect(() => {
    fetchPageData();
  }, [userId, slug, isPreview]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch the page data
      let pageData;
      if (isPreview) {
        // Preview mode: load page with auth and use draft_content_blocks
        try {
          const pages = await pagesService.getPages();
          const pagesArray = pages.pages || pages || [];
          const foundPage = pagesArray.find(p => p.slug === slug);
          if (!foundPage) {
            throw new Error('Page not found in your pages');
          }
          pageData = await pagesService.getPage(foundPage.id);
          console.log('Preview mode - loaded page:', pageData);
          console.log('Draft blocks:', pageData.draft_content_blocks);
          console.log('Published blocks:', pageData.content_blocks);
          // Use draft content blocks if available, fallback to published
          pageData.content_blocks = pageData.draft_content_blocks || pageData.content_blocks || [];
          console.log('Using content_blocks:', pageData.content_blocks);
        } catch (authError) {
          console.error('Preview mode requires authentication:', authError);
          setError('Preview mode requires you to be logged in. Please open preview from the Page Builder.');
          setLoading(false);
          return;
        }
      } else {
        // Normal mode: use public API
        pageData = await pagesService.getPublicPage(userId, slug);
      }
      setPage(pageData);

      // Fetch teacher info and portal settings for header/footer
      // If in preview mode, fetch draft settings; otherwise fetch published
      const teacherEndpoint = isPreview
        ? `/api/portal/${userId}/teacher/draft`
        : `/api/portal/${userId}/teacher`;

      const teacherResponse = await fetch(teacherEndpoint, {
        headers: isPreview ? {
          'Authorization': `Bearer ${localStorage.getItem('classtraks_token')}`
        } : {}
      });
      const teacherData = await teacherResponse.json();
      const teacher = teacherData.teacher || null;
      setTeacherInfo(teacher);

      // Extract portal settings from teacher info
      if (teacher) {
        const extractedSettings = {
          logo: teacher.logo,
          logoSize: teacher.logoSize || 'medium',
          logoIndent: teacher.logoIndent || 0,
          colorScheme: teacher.colorScheme || 'craft',
          businessName: teacher.businessName || '',
          tagline: teacher.tagline || '',
          contactEmail: teacher.contactEmail || '',
          contactPhone: teacher.contactPhone || '',
          address: teacher.address || '',
          facebook: teacher.facebook || '',
          instagram: teacher.instagram || '',
          headerContent: teacher.headerContent || '',
          footerContent: teacher.footerContent || '',
          newsStories: teacher.newsStories || [],
          upcomingProjects: teacher.upcomingProjects || [],
          // Header settings
          headerBackgroundColor: teacher.headerBackgroundColor || '#ffffff',
          headerHeight: teacher.headerHeight || 'medium',
          headerLayout: teacher.headerLayout || 'standard',
          // Site background settings
          backgroundImage: teacher.backgroundImage || '',
          backgroundPosition: teacher.backgroundPosition || 'fit',
          // Social media settings
          showSocialMedia: teacher.showSocialMedia !== false,
          socialMedia: teacher.socialMedia || {},
          // Navigation settings
          navFont: teacher.navFont || 'system',
          navFontColor: teacher.navFontColor || '#000000',
          navActiveIndicatorColor: teacher.navActiveIndicatorColor || '#3b82f6'
        };
        setPortalSettings(extractedSettings);
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  const renderBlock = (block) => {
    const BlockComponent = getBlockViewComponent(block.block_type);
    if (!BlockComponent) return null;

    // Add rounded corners for class-schedule and projects blocks
    const roundedClass = (block.block_type === 'class-schedule' || block.block_type === 'projects') ? 'rounded-lg' : '';

    return (
      <div
        className={`h-full ${getPaddingClass(block.padding, block.block_type)} ${roundedClass}`}
        style={{ backgroundColor: block.background_color }}
      >
        <BlockComponent
          content={block.content}
          userId={userId}
        />
      </div>
    );
  };

  const getBlockViewComponent = (blockType) => {
    switch (blockType) {
      case 'text':
        return TextBlockView;
      case 'image':
        return ImageBlockView;
      case 'hero':
        return HeroBlockView;
      case 'news':
        return NewsBlockView;
      case 'class-schedule':
        return ClassScheduleBlockView;
      case 'background':
        return BackgroundBlockView;
      case 'calendar':
        return CalendarBlockView;
      case 'classes':
        return ClassesBlockView;
      case 'projects':
        return ProjectsBlockView;
      case 'documents':
        return DocumentsBlockView;
      case 'tutorials':
        return TutorialsBlockView;
      default:
        return null;
    }
  };

  const getPaddingClass = (padding, blockType) => {
    // Hero blocks don't get padding wrapper - they handle their own sizing
    if (blockType === 'hero') {
      return '';
    }

    // Image and text blocks - match builder padding
    if (blockType === 'image') {
      switch (padding) {
        case 'none': return 'p-0';
        case 'small': return 'p-2';
        case 'large': return 'p-6';
        default: return 'p-4';
      }
    }

    if (blockType === 'text') {
      switch (padding) {
        case 'none': return 'p-0';
        case 'small': return 'p-4';
        case 'large': return 'p-12';
        default: return 'p-8';
      }
    }

    // Other blocks - keep existing padding
    switch (padding) {
      case 'none': return '';
      case 'small': return 'py-2 px-4';
      case 'large': return 'py-12 px-8';
      default: return 'py-4 px-6';
    }
  };

  // Calculate grid rows needed for public display
  const getGridRows = () => {
    if (!page?.content_blocks || page.content_blocks.length === 0) return 1;
    const maxRow = Math.max(...page.content_blocks.map(b => (b.grid_row || 1) + (b.grid_height || 1) - 1));
    return maxRow;
  };

  const getBlockStyle = (block) => {
    const col = block.grid_column || 1;
    const row = block.grid_row || 1;
    const width = block.grid_width || 12;
    const height = block.grid_height || 1;
    const isHero = block.block_type === 'hero';

    // Use aspect-ratio for hero blocks to maintain consistent proportions with builder
    if (isHero) {
      return {
        gridColumn: `${col} / span ${width}`,
        gridRow: `${row} / span ${height}`,
        aspectRatio: `${width} / ${height * 1.5}`
      };
    }

    return {
      gridColumn: `${col} / span ${width}`,
      gridRow: `${row} / span ${height}`,
      minHeight: `${height * 20}px`
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-500 dark:text-gray-400">Loading page...</div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">Page Not Found</h1>
          <p className="text-gray-500 dark:text-gray-400">The page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Filter out background blocks (handled at page level)
  const allBlocks = page.content_blocks?.filter(b => b.block_type !== 'background') || [];

  // Get page background styles from page settings
  const getPageBackgroundStyle = () => {
    if (page.background_type === 'color' && page.background_color) {
      return { backgroundColor: page.background_color };
    }
    if (page.background_type === 'image' && page.background_image) {
      const size = page.background_size || 100;
      const opacity = page.background_opacity !== undefined ? page.background_opacity / 100 : 1;
      const baseStyle = {
        backgroundImage: `url(${page.background_image})`,
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        opacity: opacity
      };
      switch (page.background_position) {
        case 'stretch':
          return { ...baseStyle, backgroundSize: '100% 100%' };
        case 'repeat':
          return { ...baseStyle, backgroundRepeat: 'repeat', backgroundSize: `${size}%` };
        default:
          return { ...baseStyle, backgroundSize: size === 100 ? 'cover' : `${size}%`, backgroundPosition: 'center' };
      }
    }
    return {};
  };

  // Merge page background into portal settings if page has background set
  const mergedPortalSettings = {
    ...portalSettings,
    // Override portal background with page-specific background if set
    ...(page.background_type === 'image' && page.background_image ? {
      backgroundImage: page.background_image,
      backgroundPosition: page.background_position || 'fit',
      backgroundSize: page.background_size || 100,
      backgroundOpacity: page.background_opacity !== undefined ? page.background_opacity : 100
    } : page.background_type === 'color' && page.background_color ? {
      backgroundImage: null,
      backgroundColor: page.background_color
    } : {})
  };

  return (
    <>
      {/* Preview Banner */}
      {isPreview && (
        <div className="bg-amber-500 text-white px-6 py-3 text-center font-medium flex items-center justify-center gap-2 sticky top-0 z-50 shadow-md">
          <Eye className="w-5 h-5" />
          <span>Preview Mode - You're viewing draft content that hasn't been published yet</span>
        </div>
      )}
      <PortalLayout teacherInfo={teacherInfo} portalSettings={mergedPortalSettings} userId={userId} currentPage={slug}>
        <div className="px-4 -mt-8 pb-6 relative">
        {/* Single unified grid for all blocks */}
        {allBlocks.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(48, 1fr)',
              gridAutoRows: 'minmax(20px, auto)',
              gap: '4px'
            }}
          >
            {allBlocks.map((block) => (
              <div key={block.id} className={`${block.block_type !== 'text' && block.block_type !== 'image' ? 'shadow-md' : ''} rounded-lg overflow-hidden`} style={getBlockStyle(block)}>
                {renderBlock(block)}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 px-6">
            <p className="text-gray-500 dark:text-gray-400">
              This page doesn't have any content yet.
            </p>
          </div>
        )}
      </div>
    </PortalLayout>
    </>
  );
};

export default PortalPage;
